import { useMutation } from '@tanstack/vue-query';

import {
    apiSendLoginOtp,
    apiVerifyLoginOtp,
    type BackendErrorCode,
    type BackendErrorEntity,
    type BodyType,
    type ErrorType,
    type LoginEntity,
    type SendLoginOtpBodyDto,
    type SendOtpResponseEntity,
    type VerifyLoginOtpBodyDto,
} from '~/apiClient';

// onError fires on every failure path (captcha, backend, generic) for logging and for callers that must react to a
// non-backend failure; onBackendError stays the hook to suppress the default notification for a specific code.
type SendOtpArgs = {
    data: BodyType<SendLoginOtpBodyDto>;
    onSuccess: (response: SendOtpResponseEntity) => void;
    onBackendError?: (code: BackendErrorCode) => boolean;
    onError?: (error: unknown) => void;
};

type VerifyOtpArgs = {
    data: BodyType<VerifyLoginOtpBodyDto>;
    onSuccess: (response: LoginEntity) => void | Promise<void>;
    onBackendError?: (code: BackendErrorCode) => boolean;
    onError?: (error: unknown) => void;
};

type UseOtpLoginFlowReturn = {
    turnstileContainerId: string;
    sendOtp: (args: SendOtpArgs) => Promise<void>;
    verifyOtp: (args: VerifyOtpArgs) => Promise<void>;
    isPendingSend: Ref<boolean>;
    isPendingVerify: Ref<boolean>;
    isSubmitting: ComputedRef<boolean>;
    resendCooldown: Ref<number>;
    isResendDisabled: Ref<boolean>;
    startResendCooldown: () => void;
};

export const useOtpLoginFlow = (): UseOtpLoginFlowReturn => {
    const { tt } = useTypedI18n();
    const { notifyBackendError, notifyError, notifyGenericError } = useNotification();
    const { turnstileContainerId, getToken, formatRequestOptions } = useInteractionOnlyTurnstile('login');
    const { resendCooldown, isResendDisabled, startResendCooldown } = useResendCooldown();

    const isPendingSend = ref(false);
    const isPendingVerify = ref(false);
    const isSubmitting = computed(() => isPendingVerify.value || isPendingSend.value);

    const { mutateAsync: mutateSend } = useMutation<
        Awaited<ReturnType<typeof apiSendLoginOtp>>,
        ErrorType<BackendErrorEntity>,
        { data: BodyType<SendLoginOtpBodyDto>; token: string | null },
        unknown
    >({
        mutationFn: ({ data, token }) => apiSendLoginOtp(data, formatRequestOptions(token)),
    });

    const { mutateAsync: mutateVerify } = useMutation<
        Awaited<ReturnType<typeof apiVerifyLoginOtp>>,
        ErrorType<BackendErrorEntity>,
        { data: BodyType<VerifyLoginOtpBodyDto>; token: string | null },
        unknown
    >({
        mutationFn: ({ data, token }) => apiVerifyLoginOtp(data, formatRequestOptions(token)),
    });

    const notifyCaptchaError = (): void => {
        notifyError({
            title: tt('validation.msg.captcha'),
            description: tt('notifications.tryAgain'),
        });
    };

    const sendOtp = async ({ data, onSuccess, onBackendError, onError }: SendOtpArgs): Promise<void> => {
        isPendingSend.value = true;
        let turnstileToken: string | null = null;

        try {
            try {
                turnstileToken = await getToken();
            } catch (captchaError) {
                onError?.(captchaError);
                notifyCaptchaError();

                return;
            }

            await mutateSend({ data, token: turnstileToken }).then(response => {
                onSuccess(response);
            });
        } catch (apiError) {
            onError?.(apiError);

            if (isFetchError(apiError) && isBackendError(apiError.data)) {
                if (onBackendError?.(apiError.data.error.code)) return;

                notifyBackendError({ error: apiError.data.error });

                return;
            }

            notifyGenericError();
        } finally {
            isPendingSend.value = false;
        }
    };

    const verifyOtp = async ({ data, onSuccess, onBackendError, onError }: VerifyOtpArgs): Promise<void> => {
        isPendingVerify.value = true;
        let turnstileToken: string | null = null;

        try {
            try {
                turnstileToken = await getToken();
            } catch (captchaError) {
                onError?.(captchaError);
                notifyCaptchaError();

                return;
            }

            await mutateVerify({ data, token: turnstileToken }).then(async response => {
                await onSuccess(response);
            });
        } catch (apiError) {
            onError?.(apiError);

            if (isFetchError(apiError) && isBackendError(apiError.data)) {
                if (onBackendError?.(apiError.data.error.code)) return;

                notifyBackendError({ error: apiError.data.error });

                return;
            }

            notifyGenericError();
        } finally {
            isPendingVerify.value = false;
        }
    };

    return {
        turnstileContainerId,
        sendOtp,
        verifyOtp,
        isPendingSend,
        isPendingVerify,
        isSubmitting,
        resendCooldown,
        isResendDisabled,
        startResendCooldown,
    };
};
